<template>
  <div
    class="select-menu"
    :class="{ 'select-menu--required': props.required }"
    :data-private-info="true"
  >
    <label :id="labelId" class="visually-hidden">{{ placeholder }}</label>

    <var-style-provider :style-vars="styleVars">
      <var-select
        ref="selectRef"
        role="combobox"
        aria-haspopup="listbox"
        :aria-expanded="isOpen"
        :aria-controls="listboxId"
        :aria-labelledby="labelId"
        v-model="inputValue"
        :aria-required="props.required"
        :placeholder="placeholder"
        :variant="variant"
        :offset-y="offsetY"
        :line="true"
        :rules="validateInput"
        @change="handleChange"
        @click="handleClick"
        @blur="handleBlur"
        @close="handleClose"
        @keydown="onTriggerKeydown"
        :data-testid="props.selectTestId"
      >
        <div role="listbox" :id="listboxId" :aria-labelledby="labelId">
          <var-option
            v-for="(option, index) in options"
            :key="option.value"
            :label="option.label"
            :value="option.value"
            :id="getOptionId(index)"
            role="option"
            :aria-selected="inputValue === option.value"
          />
        </div>

        <template #selected>
          <span>
            {{ options.find((option) => option.value == inputValue)?.label }}
          </span>
        </template>
      </var-select>
    </var-style-provider>
  </div>
</template>

<script lang="ts" setup>
import './SelectMenu.scss';
import { type LDTheme, type SelectMenuOption } from 'ewi-ui-helpers/types';

defineOptions({
  inheritAttrs: false,
});

export interface SelectMenuProps {
  options: SelectMenuOption[];
  modelValue?: any;
  theme?: LDTheme;
  placeholder?: string;
  variant?: 'outlined' | 'standard';
  offsetY?: number;
  selectTestId?: string;
  customErrorColor?: string;
  validateInput?: (value: string) => string | boolean;
  required?: boolean;
}

const props = withDefaults(defineProps<SelectMenuProps>(), {
  options: () => [],
  placeholder: 'Please Select one of the options',
  modelValue: null,
  theme: 'light',
  variant: 'standard',
  offsetY: 34,
  selectTestId: 'select-menu',
});

// !!!!!! Uncomment the following lines if you want to handle keydown events on the select trigger
// after resolving the key down and key up Varlet limitation !!!!

// Add in your setup or mounted hook
// onMounted(() => {
//   if (selectRef.value) {
//     selectRef.value.$el.addEventListener('keydown', onTriggerKeydown);
//   }
// });

// // Clean up when component unmounts
// onBeforeUnmount(() => {
//   if (selectRef.value) {
//     selectRef.value.$el.removeEventListener('keydown', onTriggerKeydown);
//   }
// });

const onTriggerKeydown = (event: KeyboardEvent) => {
  switch (
    event.key
    //!!!!!!!!!!!! CURRENTLY VARLET DOESN'T SUPPORT OPENING THE MENU ON KEY DOWN OR KEY UP !!!!!

    // case 'ArrowDown':
    //   console.log('ArrowDown pressed');
    //   isOpen.value = true;
    //   console.log('isOpen value:', isOpen.value);
    //   focusedIndex.value = (focusedIndex.value + 1) % props.options.length;
    //   break;
    // case 'ArrowUp':
    //   isOpen.value = true;
    //   focusedIndex.value =
    //     (focusedIndex.value - 1 + props.options.length) % props.options.length;
    //   break;
  ) {
  }
};

const lightTheme = {
  '--field-decorator-text-color': 'var(--SysLightOnSurfaceVariant)',
  '--field-decorator-error-color': 'var(--SysLightError)',
  '--field-decorator-blur-color': 'var(--SysLightOnSurface)',
  '--field-decorator-focus-color': 'var(--SysLightPrimary)',
  '--field-background-color': 'var(--SysLightInverseOnSurface)',
  '--form-details-error-message-color':
    props.customErrorColor || 'var(--SysLightError)',
};

const darkTheme = {
  '--field-decorator-text-color': 'var(--SysDarkOnSurfaceVariant)',
  '--field-decorator-error-color': 'var(--SysDarkError)',
  '--field-decorator-blur-color': 'var(--SysDarkOnSurface)',
  '--field-decorator-focus-color': 'var(--SysDarkPrimary)',
  '--field-background-color': 'var(--SysDarkInverseOnSurface)',
  '--form-details-error-message-color':
    props.customErrorColor || 'var(--SysDarkError)',
};

const styleVars = ref(props.theme === 'light' ? lightTheme : darkTheme);
const emit = defineEmits(['update:modelValue', 'change']);
const inputValue = ref(props.modelValue);
const selectRef = ref();
const listboxId = `listbox-${Math.random().toString(36).substring(2)}`;
const labelId = `label-${Math.random().toString(36).substring(2)}`;
const getOptionId = (index: number) => `${listboxId}-option-${index}`;
const isOpen = ref(false);

watch(
  () => props.modelValue,
  (newValue) => {
    inputValue.value = newValue;
  },
);

const handleChange = (value: any) => {
  isOpen.value = false;
  emit('update:modelValue', value);
  emit('change', value);
};

const handleClick = () => {
  isOpen.value = true;
};

const handleBlur = () => {
  isOpen.value = false;
};

const handleClose = () => {
  isOpen.value = false;
};

onMounted(() => {
  selectRef.value.$el.addEventListener('keydown', (event: KeyboardEvent) => {
    setTimeout(() => {
      isOpen.value = selectRef.value.showMenu || event.key === ' ';
    }, 10);
  });
});

defineExpose({
  validate: () => {
    return selectRef.value?.validate();
  },
});
</script>
